import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, InputAdornment, Popover, Tooltip, Typography } from '@mui/material';
import { Calculate, Close, Backspace } from '@mui/icons-material';

const OPERATORS = ['+', '-', '*', '/'];
const DISPLAY_SYMBOL = { '*': '×', '/': '÷', '-': '−', '+': '+' };

// Evaluates "20*30+15*10" with × ÷ before + −. A trailing operator is ignored.
// Returns null for empty, malformed or divide-by-zero input.
const evaluate = (expr) => {
  const tokens = expr.match(/\d*\.?\d+|\d+\.|[+\-*/]/g);
  if (!tokens) return null;
  if (OPERATORS.includes(tokens[tokens.length - 1])) tokens.pop();
  if (!tokens.length || OPERATORS.includes(tokens[0])) return null;

  const terms = [parseFloat(tokens[0])];
  const ops = [];
  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i];
    const num = parseFloat(tokens[i + 1]);
    if (!OPERATORS.includes(op) || Number.isNaN(num)) return null;
    if (op === '*' || op === '/') {
      if (op === '/' && num === 0) return null;
      const prev = terms.pop();
      terms.push(op === '*' ? prev * num : prev / num);
    } else {
      ops.push(op);
      terms.push(num);
    }
  }
  const total = ops.reduce((acc, op, i) => (op === '+' ? acc + terms[i + 1] : acc - terms[i + 1]), terms[0]);
  return Number.isFinite(total) ? Math.round(total * 10000) / 10000 : null;
};

const prettify = (expr) => expr.replace(/[+\-*/]/g, (op) => ` ${DISPLAY_SYMBOL[op]} `);

const SqFtCalculatorPanel = ({ initialValue, fieldLabel, decimals, onApply, onClose }) => {
  const { t } = useTranslation();
  const seed = Number(initialValue) > 0 ? String(initialValue) : '';
  const [expr, setExpr] = useState(seed);
  // The seeded field value (or a finished result) is replaced by the next digit.
  const [replaceOnDigit, setReplaceOnDigit] = useState(Boolean(seed));

  const lastNumber = expr.split(/[+\-*/]/).pop();
  const endsWithOperator = OPERATORS.includes(expr.slice(-1));
  const result = evaluate(expr);
  const hasError = expr !== '' && !endsWithOperator && result === null;
  const factor = 10 ** decimals;
  const applyValue = result !== null && result >= 0 ? Math.round(result * factor) / factor : null;

  const pressDigit = (d) => {
    if (replaceOnDigit) {
      setExpr(d === '.' ? '0.' : d);
      setReplaceOnDigit(false);
      return;
    }
    if (d === '.') {
      if (lastNumber.includes('.')) return;
      setExpr(expr + (lastNumber === '' ? '0.' : '.'));
      return;
    }
    setExpr(lastNumber === '0' ? expr.slice(0, -1) + d : expr + d);
  };

  const pressOperator = (op) => {
    setReplaceOnDigit(false);
    if (expr === '') {
      setExpr(`0${op}`);
    } else if (endsWithOperator) {
      setExpr(expr.slice(0, -1) + op);
    } else {
      setExpr(expr + op);
    }
  };

  const pressEquals = () => {
    if (result === null) return;
    setExpr(String(result));
    setReplaceOnDigit(true);
  };

  const pressBackspace = () => {
    setReplaceOnDigit(false);
    setExpr(expr.slice(0, -1));
  };

  const pressClear = () => {
    setReplaceOnDigit(false);
    setExpr('');
  };

  const apply = () => {
    if (applyValue === null) return;
    onApply(applyValue);
    onClose();
  };

  const handleKeyDown = (e) => {
    const { key } = e;
    if (/^[0-9.]$/.test(key)) pressDigit(key);
    else if (key === '+' || key === '-' || key === '*' || key === '/') pressOperator(key);
    else if (key === 'x' || key === 'X') pressOperator('*');
    else if (key === 'Enter' || key === '=') {
      if (replaceOnDigit && applyValue !== null) apply();
      else pressEquals();
    } else if (key === 'Backspace') pressBackspace();
    else if (key === 'Delete' || key === 'c' || key === 'C') pressClear();
    else return;
    e.preventDefault();
  };

  const keyBtn = {
    minWidth: 0,
    minHeight: 44,
    fontSize: '1.1rem',
    fontWeight: 600,
    borderRadius: 1,
    boxShadow: 'none'
  };
  const digitSx = { ...keyBtn, bgcolor: '#f5f7fa', color: '#1a1a1a', '&:hover': { bgcolor: '#e6eaf0' } };
  const opSx = { ...keyBtn, bgcolor: '#dfe7f3', color: '#000062', '&:hover': { bgcolor: '#cbd7ea' } };

  const digit = (d, extraSx = {}) => (
    <Button
      key={d}
      onClick={() => pressDigit(d)}
      sx={{ ...digitSx, ...extraSx }}
      aria-label={d === '.' ? t('sqftCalculator.decimal', 'Decimal point') : d}
    >
      {d}
    </Button>
  );
  const operator = (op, label) => (
    <Button key={op} onClick={() => pressOperator(op)} sx={opSx} aria-label={label}>
      {DISPLAY_SYMBOL[op]}
    </Button>
  );

  const title = t('sqftCalculator.title', 'Square Footage Calculator');

  return (
    <Box
      role="dialog"
      aria-label={fieldLabel ? `${title} - ${fieldLabel}` : title}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      sx={{ width: 272, maxWidth: 'calc(100vw - 32px)', p: 1.5, outline: 'none' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#000062' }}>{title}</Typography>
          {fieldLabel && (
            <Typography variant="caption" color="textSecondary" noWrap component="div">{fieldLabel}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label={t('sqftCalculator.close', 'Close calculator')}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box
        role="status"
        aria-live="polite"
        sx={{ bgcolor: '#1f2a37', color: '#fff', borderRadius: 1, px: 1.5, py: 1, mb: 1, textAlign: 'right', minHeight: 64 }}
      >
        <Typography variant="caption" component="div" sx={{ color: '#b8c4d4', minHeight: 18, wordBreak: 'break-all' }}>
          {prettify(expr) || ' '}
        </Typography>
        <Typography variant="h5" component="div" sx={{ fontWeight: 700, wordBreak: 'break-all', color: hasError ? '#ffb4a8' : '#fff' }}>
          {hasError
            ? t('sqftCalculator.error', "Can't calculate")
            : (result !== null ? result.toLocaleString() : '0')}
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75 }}>
        <Button onClick={pressClear} sx={{ ...opSx, color: '#b3261e' }} aria-label={t('sqftCalculator.clear', 'Clear')}>C</Button>
        <Button onClick={pressBackspace} sx={opSx} aria-label={t('sqftCalculator.backspace', 'Delete last character')}>
          <Backspace fontSize="small" />
        </Button>
        {operator('/', t('sqftCalculator.divide', 'Divide'))}
        {operator('*', t('sqftCalculator.multiply', 'Multiply'))}
        {['7', '8', '9'].map(d => digit(d))}
        {operator('-', t('sqftCalculator.subtract', 'Subtract'))}
        {['4', '5', '6'].map(d => digit(d))}
        {operator('+', t('sqftCalculator.add', 'Add'))}
        {['1', '2', '3'].map(d => digit(d))}
        <Button
          onClick={pressEquals}
          sx={{ ...keyBtn, gridRow: 'span 2', bgcolor: '#2e7d32', color: '#fff', '&:hover': { bgcolor: '#256628' } }}
          aria-label={t('sqftCalculator.equals', 'Equals')}
        >
          =
        </Button>
        {digit('0', { gridColumn: 'span 2' })}
        {digit('.')}
      </Box>

      <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 1 }}>
        {t('sqftCalculator.hint', 'Tip: length × width, then + for each extra area.')}
      </Typography>

      <Button
        fullWidth
        variant="contained"
        onClick={apply}
        disabled={applyValue === null}
        sx={{ mt: 1, bgcolor: '#000062', '&:hover': { bgcolor: '#00004a' } }}
        aria-label={applyValue === null
          ? t('sqftCalculator.useDisabled', 'Enter a calculation to use its result')
          : t('sqftCalculator.use', 'Use {{value}} sq ft', { value: applyValue.toLocaleString() })}
      >
        {applyValue === null
          ? t('sqftCalculator.useEmpty', 'Use result')
          : t('sqftCalculator.use', 'Use {{value}} sq ft', { value: applyValue.toLocaleString() })}
      </Button>
    </Box>
  );
};

// End adornment for a square-footage TextField: a calculator icon that opens
// the popup and writes the result back through onApply.
export const SqFtCalculatorAdornment = ({ value, onApply, fieldLabel, decimals = 0 }) => {
  const { t } = useTranslation();
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const openLabel = t('sqftCalculator.open', 'Open calculator for {{field}}', { field: fieldLabel || '' });

  return (
    <InputAdornment position="end">
      <Tooltip title={t('sqftCalculator.tooltip', 'Calculator')}>
        <IconButton
          ref={buttonRef}
          edge="end"
          size="small"
          onClick={() => setOpen(true)}
          aria-label={openLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{ color: '#000062' }}
        >
          <Calculate fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionProps={{ onEntered: () => panelRef.current?.querySelector('[role="dialog"]')?.focus() }}
      >
        <div ref={panelRef}>
          {open && (
            <SqFtCalculatorPanel
              initialValue={value}
              fieldLabel={fieldLabel}
              decimals={decimals}
              onApply={onApply}
              onClose={() => setOpen(false)}
            />
          )}
        </div>
      </Popover>
    </InputAdornment>
  );
};

export default SqFtCalculatorAdornment;
